/**
 * FileDropzone — accessible drag-and-drop file picker.
 *
 * Purely a UX primitive. No upload logic; callers handle the file themselves.
 * Keyboard users activate the visible "Choose file" button (focuses the hidden
 * <input type="file">).
 *
 * Props:
 *  - accept      (string)          MIME/ext allow-list for the <input>, e.g. "audio/*"
 *  - maxBytes    (number|null)     Reject files above this size client-side (null = no cap)
 *  - disabled    (bool)            Disable all interactions
 *  - file        (File|null)       Currently-selected file (for controlled use)
 *  - onFile      (fn)              Called with the selected File (or null when cleared)
 *  - helpText    (string)          Rendered below the dropzone
 *  - error       (string|null)     Optional error to display inside the dropzone
 */

import { useCallback, useId, useRef, useState } from 'react';
import { UploadCloud, X } from 'lucide-react';

const humanBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const FileDropzone = ({
  accept = '*',
  maxBytes = null,
  disabled = false,
  file = null,
  onFile,
  helpText = '',
  error = null,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);
  const describedById = useId();

  const validate = useCallback(
    (candidate) => {
      if (!candidate) return null;
      if (maxBytes != null && candidate.size > maxBytes) {
        return `File is ${humanBytes(candidate.size)}; maximum allowed is ${humanBytes(maxBytes)}.`;
      }
      return null;
    },
    [maxBytes],
  );

  const acceptFile = useCallback(
    (candidate) => {
      const invalid = validate(candidate);
      if (invalid) {
        setLocalError(invalid);
        onFile?.(null);
        return;
      }
      setLocalError(null);
      onFile?.(candidate);
    },
    [onFile, validate],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      if (disabled) return;
      const dropped = e.dataTransfer?.files?.[0];
      if (dropped) acceptFile(dropped);
    },
    [disabled, acceptFile],
  );

  const handleSelect = (e) => {
    const picked = e.target.files?.[0];
    if (picked) acceptFile(picked);
    e.target.value = '';
  };

  const clear = () => {
    setLocalError(null);
    onFile?.(null);
  };

  const visibleError = error ?? localError;

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        aria-describedby={describedById}
        className={[
          'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-gray-50',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
          visibleError ? 'border-red-400 bg-red-50' : '',
        ].join(' ')}
      >
        <UploadCloud size={28} className="mx-auto text-gray-400 mb-2" aria-hidden />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-800">
            <span className="font-medium">{file.name}</span>
            <span className="text-gray-500">({humanBytes(file.size)})</span>
            {!disabled && (
              <button
                type="button"
                onClick={clear}
                className="ml-1 inline-flex items-center text-gray-400 hover:text-red-600"
                aria-label="Remove selected file"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 mb-2">Drag a file here, or</p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 text-sm border border-gray-300 bg-white rounded px-3 py-1.5 hover:bg-gray-100 disabled:opacity-50"
            >
              Choose file
            </button>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleSelect}
          disabled={disabled}
          className="sr-only"
        />
      </div>

      {visibleError ? (
        <p className="mt-2 text-xs text-red-600" role="alert">{visibleError}</p>
      ) : helpText ? (
        <p id={describedById} className="mt-2 text-xs text-gray-500">{helpText}</p>
      ) : null}
    </div>
  );
};

export default FileDropzone;
